// src/public/tests/LogicalProcessTest.js
import { useState, useEffect } from 'react';
import {
  Paper, Box, Typography, Button, LinearProgress,
  FormControl, Select, MenuItem, Stack, Divider, Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';

// Preguntas de Proceso Lógico - ESPAÑOL
export const logicalProcessQuestionsES = [
  {
    id: 1,
    statement: [
      "En un edificio de apartamentos vive una familia en cada uno de los cuatro pisos;",
      "Los Sánchez viven encima de los Jaen;",
      "Los Botero viven encima de los Vásquez;",
      "Los Sánchez viven debajo de los Vásquez.",
    ],
    subQuestions: [
      { question: "¿Cuál familia vive más arriba, los Botero o los Sánchez?", options: ["Botero", "Sánchez"], correctAnswer: 0 },
      { question: "¿Cuál familia vive más arriba, los Botero o los Jaen?", options: ["Botero", "Jaen"], correctAnswer: 0 },
      { question: "¿Cuál familia vive más arriba, los Jaen o los Vásquez?", options: ["Jaen", "Vásquez"], correctAnswer: 1 },
    ],
  },
  {
    id: 2,
    statement: [
      "Las figuras son siempre negras o blancas;",
      "El círculo y el cuadrado son siempre de diferente color;",
      "El triángulo y el rectángulo son siempre del mismo color;",
      "El cuadrado y el rectángulo son siempre de diferente color.",
    ],
    subQuestions: [
      { question: "Si el cuadrado es blanco, ¿cuál es el color del triángulo?", options: ["Blanco", "Negro"], correctAnswer: 1 },
      { question: "Si el círculo es blanco, ¿cuál es el color del rectángulo?", options: ["Blanco", "Negro"], correctAnswer: 0 },
      { question: "Si el rectángulo es negro, ¿cuál es el color del triángulo?", options: ["Blanco", "Negro"], correctAnswer: 1 },
    ],
  },
  {
    id: 3,
    statement: [
      "Si el abrigo es azul, el sombrero es amarillo;",
      "Si el abrigo no es amarillo, el sombrero es verde;",
      "Si el sombrero es rojo, el abrigo es azul;",
      "El sombrero es siempre azul o rojo.",
    ],
    subQuestions: [
      { question: "¿Cuál es el color del sombrero?", options: ["Azul", "Amarillo", "Rojo", "Verde"], correctAnswer: 0 },
      { question: "¿Cuál es el color del abrigo?", options: ["Amarillo", "Azul", "Verde", "Rojo"], correctAnswer: 0 },
    ],
  },
  {
    id: 4,
    statement: [
      "Si A > C, entonces B no es < C;",
      "Si B > C, entonces A = B;",
      "A es > C;",
      "Si A = B, entonces A < C.",
    ],
    subQuestions: [
      { question: "¿Cuál es la relación entre A y B?", options: ["A > B", "A = B", "A < B"], correctAnswer: 0 },
      { question: "¿Cuál es la relación entre A y C?", options: ["A > C", "A = C", "A < C"], correctAnswer: 0 },
      { question: "¿Cuál es la relación entre B y C?", options: ["B = C", "B < C", "B > C"], correctAnswer: 0 },
    ],
  },
  {
    id: 5,
    statement: [
      "Cada uno de los individuos A, B y C pertenece a una de las clases M, N o P;",
      "Si B pertenece a la clase N, entonces C pertenece a la clase M;",
      "Si C pertenece a la clase N, entonces B pertenece a la clase N;",
      "Si A no pertenece a la clase N, entonces C no pertenece a la clase N;",
      "C no pertenece a la clase M;",
      "Si B pertenece a la clase P, entonces C pertenece a la clase P.",
    ],
    subQuestions: [
      { question: "¿A qué clase pertenece A?", options: ["M", "N", "P"], correctAnswer: 1 },
      { question: "¿A qué clase pertenece B?", options: ["M", "N", "P"], correctAnswer: 0 },
      { question: "¿A qué clase pertenece C?", options: ["M", "N", "P"], correctAnswer: 1 },
    ],
  },
  {
    id: 6,
    statement: [
      "Cada uno de los símbolos X, Y y Z tiene un solo valor;",
      "Si Y = 5, entonces Z = 6;",
      "Si Z no es 9, entonces X no es 3;",
      "Si X no es 2, entonces Y = Z;",
      "Si Z = 7, entonces X = 3;",
      "Si Y no es 5, entonces Z = 7.",
    ],
    subQuestions: [
      { question: "¿Cuál es el valor de X?", options: [2, 3, 5, 6, 7, 9], correctAnswer: 0 },
      { question: "¿Cuál es el valor de Y?", options: [2, 3, 5, 6, 7, 9], correctAnswer: 2 },
      { question: "¿Cuál es el valor de Z?", options: [2, 3, 5, 6, 7, 9], correctAnswer: 3 },
    ],
  },
];

// Preguntas de Proceso Lógico - ENGLISH
export const logicalProcessQuestionsEN = [
  {
    id: 1,
    statement: [
      "In an apartment building, one family lives on each of the four floors;",
      "The Sánchez family lives above the Jaen family;",
      "The Botero family lives above the Vásquez family;",
      "The Sánchez family lives below the Vásquez family.",
    ],
    subQuestions: [
      { question: "Which family lives higher, the Botero or the Sánchez?", options: ["Botero", "Sánchez"], correctAnswer: 0 },
      { question: "Which family lives higher, the Botero or the Jaen?", options: ["Botero", "Jaen"], correctAnswer: 0 },
      { question: "Which family lives higher, the Jaen or the Vásquez?", options: ["Jaen", "Vásquez"], correctAnswer: 1 },
    ],
  },
  {
    id: 2,
    statement: [
      "The figures are always black or white;",
      "The circle and the square are always different colors;",
      "The triangle and the rectangle are always the same color;",
      "The square and the rectangle are always different colors.",
    ],
    subQuestions: [
      { question: "If the square is white, what is the color of the triangle?", options: ["White", "Black"], correctAnswer: 1 },
      { question: "If the circle is white, what is the color of the rectangle?", options: ["White", "Black"], correctAnswer: 0 },
      { question: "If the rectangle is black, what is the color of the triangle?", options: ["White", "Black"], correctAnswer: 1 },
    ],
  },
  {
    id: 3,
    statement: [
      "If the coat is blue, the hat is yellow;",
      "If the coat is not yellow, the hat is green;",
      "If the hat is red, the coat is blue;",
      "The hat is always blue or red.",
    ],
    subQuestions: [
      { question: "What is the color of the hat?", options: ["Blue", "Yellow", "Red", "Green"], correctAnswer: 0 },
      { question: "What is the color of the coat?", options: ["Yellow", "Blue", "Green", "Red"], correctAnswer: 0 },
    ],
  },
  {
    id: 4,
    statement: [
      "If A > C, then B is not < C;",
      "If B > C, then A = B;",
      "A is > C;",
      "If A = B, then A < C.",
    ],
    subQuestions: [
      { question: "What is the relationship between A and B?", options: ["A > B", "A = B", "A < B"], correctAnswer: 0 },
      { question: "What is the relationship between A and C?", options: ["A > C", "A = C", "A < C"], correctAnswer: 0 },
      { question: "What is the relationship between B and C?", options: ["B = C", "B < C", "B > C"], correctAnswer: 0 },
    ],
  },
  {
    id: 5,
    statement: [
      "Each of the individuals A, B, and C belongs to one of the classes M, N, or P;",
      "If B belongs to class N, then C belongs to class M;",
      "If C belongs to class N, then B belongs to class N;",
      "If A does not belong to class N, then C does not belong to class N;",
      "C does not belong to class M;",
      "If B belongs to class P, then C belongs to class P.",
    ],
    subQuestions: [
      { question: "Which class does A belong to?", options: ["M", "N", "P"], correctAnswer: 1 },
      { question: "Which class does B belong to?", options: ["M", "N", "P"], correctAnswer: 0 },
      { question: "Which class does C belong to?", options: ["M", "N", "P"], correctAnswer: 1 },
    ],
  },
  {
    id: 6,
    statement: [
      "Each of the symbols X, Y, and Z has only one value;",
      "If Y = 5, then Z = 6;",
      "If Z is not 9, then X is not 3;",
      "If X is not 2, then Y = Z;",
      "If Z = 7, then X = 3;",
      "If Y is not 5, then Z = 7.",
    ],
    subQuestions: [
      { question: "What is the value of X?", options: [2, 3, 5, 6, 7, 9], correctAnswer: 0 },
      { question: "What is the value of Y?", options: [2, 3, 5, 6, 7, 9], correctAnswer: 2 },
      { question: "What is the value of Z?", options: [2, 3, 5, 6, 7, 9], correctAnswer: 3 },
    ],
  },
];

const LogicalProcessTest = ({
  responses = {},
  onAnswerChange,
  currentQuestion,
  onQuestionChange,
  onComplete,
  saving,
  language = 'es',
  timeRemaining = null
}) => {
  const { t } = useTranslation();
  const [subAnswers, setSubAnswers] = useState({});

  // Seleccionar preguntas según idioma
  const logicalProcessQuestions = language === 'es'
    ? logicalProcessQuestionsES
    : logicalProcessQuestionsEN;

  const totalQuestions = logicalProcessQuestions.length;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;
  const question = logicalProcessQuestions[currentQuestion];

  // Cargar respuestas guardadas al cambiar de pregunta
  useEffect(() => {
    const savedAnswers = responses[question.id];
    if (savedAnswers) {
      setSubAnswers(savedAnswers);
    } else {
      setSubAnswers({});
    }
  }, [currentQuestion, question.id, responses]);

  // Manejar cambio en una sub-pregunta
  const handleSubAnswerChange = (subIndex, value) => {
    const newSubAnswers = {
      ...subAnswers,
      [subIndex]: value
    };
    setSubAnswers(newSubAnswers);
    // NO guardamos inmediatamente, solo actualizamos el estado local
  };

  // Verificar si todas las sub-preguntas están respondidas
  const allAnswered = () => {
    return question.subQuestions.every((_, idx) =>
      subAnswers[idx] !== undefined && subAnswers[idx] !== ''
    );
  };

  // Navegar a la siguiente pregunta o finalizar
  const handleNext = () => {
    // Primero actualizar el estado del padre
    onAnswerChange(question.id, subAnswers);

    if (currentQuestion < totalQuestions - 1) {
      // Construir todas las respuestas incluyendo la actual
      const allResponses = { ...responses, [question.id]: subAnswers };
      onQuestionChange(currentQuestion + 1, allResponses);
    } else {
      const allResponses = { ...responses, [question.id]: subAnswers };
      onComplete(allResponses);
    }
  };

  // Saltar pregunta
  const handleSkip = () => {
    onAnswerChange(question.id, {});

    if (currentQuestion < totalQuestions - 1) {
      const allResponses = { ...responses, [question.id]: {} };
      onQuestionChange(currentQuestion + 1, allResponses);
    } else {
      const allResponses = { ...responses, [question.id]: {} };
      onComplete(allResponses);
    }
  };

  const canGoNext = allAnswered();
  const isTimeExpired = timeRemaining !== null && timeRemaining <= 0;

  return (
    <Paper elevation={3} sx={{ p: 4 }}>
      {/* Header con progreso */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t('reasoningTests.questionProgress', {
            current: currentQuestion + 1,
            total: totalQuestions
          })}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 8, borderRadius: 1 }}
        />
      </Box>

      {/* Mensaje de tiempo expirado */}
      {isTimeExpired && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('reasoningTests.timeExpired')}
        </Alert>
      )}

      {/* Enunciado */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
          {t('reasoningTests.itIsKnownThat')}
        </Typography>

        <Box sx={{ pl: 2 }}>
          {question.statement.map((line, idx) => (
            <Typography
              key={idx}
              variant="body1"
              sx={{ mb: 1 }}
            >
              {line}
            </Typography>
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Sub-preguntas */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          {t('reasoningTests.questions')}:
        </Typography>

        <Stack spacing={3} sx={{ mt: 3 }}>
          {question.subQuestions.map((subQ, idx) => (
            <Box key={idx}>
              <Typography variant="body1" gutterBottom sx={{ mb: 1.5 }}>
                {subQ.question}
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={subAnswers[idx] ?? ''}
                  onChange={(e) => handleSubAnswerChange(idx, e.target.value)}
                  displayEmpty
                  disabled={isTimeExpired}
                  sx={{
                    '& .MuiSelect-select': {
                      color: subAnswers[idx] !== undefined ? 'text.primary' : 'text.secondary'
                    }
                  }}
                >
                  <MenuItem value="" disabled>
                    {t('reasoningTests.selectAnOption')}
                  </MenuItem>
                  {subQ.options.map((option, optIdx) => (
                    <MenuItem key={optIdx} value={optIdx}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Botones de navegación */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="text"
          onClick={handleSkip}
          disabled={saving || isTimeExpired}
        >
          {t('reasoningTests.skipQuestion')}
        </Button>

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!canGoNext || saving || isTimeExpired}
          size="large"
        >
          {currentQuestion === totalQuestions - 1
            ? t('reasoningTests.finish')
            : t('reasoningTests.next')
          }
        </Button>
      </Box>

      {/* Indicador de guardado */}
      {saving && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 2 }}
        >
          {t('reasoningTests.savingProgress')}
        </Typography>
      )}
    </Paper>
  );
};

export default LogicalProcessTest;